import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Pencil, Plus, Mail, Phone, MapPin } from "lucide-react";
import { api } from "../lib/api";
import { money, fmtDate, customerName } from "../lib/format";
import { StatusBadge } from "../components/StatusBadge";
import { CustomerDialog } from "../components/CustomerDialog";
import { Button } from "../components/ui/button";

const Stat = ({ label, value, sub, testId, accent }) => (
  <div data-testid={testId} className={`rounded-lg border bg-white p-4 ${accent ? "border-l-4 border-red-600" : "border-zinc-200"}`}>
    <div className="eyebrow">{label}</div>
    <div className="mt-1 font-display text-3xl font-bold">{value}</div>
    {sub && <div className="font-mono text-xs text-zinc-500">{sub}</div>}
  </div>
);

const Info = ({ icon: Icon, children }) => children ? <div className="flex items-start gap-2 text-sm"><Icon className="mt-0.5 h-4 w-4 shrink-0 text-red-600" /><span>{children}</span></div> : null;

export default function CustomerDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [c, setC] = useState(null);
  const [quotes, setQuotes] = useState([]);
  const [editOpen, setEditOpen] = useState(false);

  const load = useCallback(() => {
    api.get(`/customers/${id}`).then((r) => setC(r.data)).catch(() => navigate("/customers"));
    api.get("/quotes", { params: { customer_id: id } }).then((r) => setQuotes(r.data));
  }, [id, navigate]);
  useEffect(() => { load(); }, [load]);
  if (!c) return null;

  const by = (s) => quotes.filter((q) => q.status === s);
  const sum = (l) => l.reduce((t, q) => t + q.total, 0);
  const decided = by("approved").length + by("declined").length;
  const addr = (p = "") => [c[`${p}street`], c[`${p}suburb`], c[`${p}state`], c[`${p}postcode`]].filter(Boolean).join(", ");

  return (
    <div className="animate-rise">
      <button data-testid="back-to-customers" onClick={() => navigate("/customers")} className="mb-4 flex items-center gap-1 text-sm text-zinc-500 hover:text-zinc-900"><ArrowLeft className="h-4 w-4" />All customers</button>
      <div className="mb-6 flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="eyebrow mb-1 capitalize">{c.customer_type} customer{c.abn ? ` · ABN ${c.abn}` : ""}</div>
          <h1 data-testid="customer-detail-name" className="font-display text-4xl font-bold uppercase tracking-tight sm:text-5xl">{customerName(c)}</h1>
        </div>
        <div className="flex gap-2">
          <Button data-testid="customer-detail-edit" variant="outline" onClick={() => setEditOpen(true)}><Pencil className="mr-1 h-4 w-4" />Edit</Button>
          <Button data-testid="customer-detail-new-quote" onClick={() => navigate(`/quotes/new?customer=${c.id}`)} className="bg-red-600 hover:bg-red-700"><Plus className="mr-1 h-4 w-4" />New quote</Button>
        </div>
      </div>

      <div className="grid gap-6 lg:grid-cols-[300px_1fr]">
        <div className="space-y-3 rounded-lg border border-zinc-200 bg-white p-5 lg:self-start" data-testid="customer-detail-contact">
          <div className="eyebrow">Contact</div>
          <div className="font-semibold">{c.contact_name}</div>
          <Info icon={Mail}>{c.email}</Info>
          <Info icon={Phone}>{[c.phone, c.mobile].filter(Boolean).join(" · ")}</Info>
          <Info icon={MapPin}>{addr()}</Info>
          {!c.site_same_as_billing && <><div className="eyebrow pt-2">Site</div><Info icon={MapPin}>{addr("site_")}</Info></>}
          {c.notes && <><div className="eyebrow pt-2">Notes</div><p className="whitespace-pre-wrap text-sm text-zinc-600">{c.notes}</p></>}
        </div>

        <div className="min-w-0 space-y-6">
          <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
            <Stat testId="customer-stat-quotes" label="Quotes" value={quotes.length} sub={`${money(sum(quotes))} quoted`} />
            <Stat testId="customer-stat-approved" accent label="Approved value" value={money(sum(by("approved")))} sub={`${by("approved").length} approved`} />
            <Stat testId="customer-stat-win-rate" label="Approval rate" value={decided ? `${Math.round((by("approved").length / decided) * 100)}%` : "—"} sub={`${by("declined").length} declined`} />
            <Stat testId="customer-stat-pending" label="Pending" value={by("draft").length + by("sent").length} sub={money(sum(by("draft")) + sum(by("sent")))} />
          </div>
          <div className="overflow-hidden rounded-lg border border-zinc-200 bg-white">
            <div className="border-b border-zinc-200 px-5 py-3 font-display text-lg font-bold uppercase">Quote history</div>
            <table className="w-full text-sm" data-testid="customer-quotes-table">
              <thead className="border-b border-zinc-200 bg-slate-50 text-left font-mono text-[11px] uppercase tracking-wider text-zinc-500">
                <tr><th className="px-5 py-2.5">Number</th><th className="px-3">Title</th><th className="px-3">Status</th><th className="px-3">Created</th><th className="px-3">Decided</th><th className="px-5 text-right">Total inc GST</th></tr>
              </thead>
              <tbody>
                {quotes.map((q) => (
                  <tr key={q.id} data-testid={`customer-quote-row-${q.number}`} onClick={() => navigate(`/quotes/${q.id}`)} className="cursor-pointer border-b border-zinc-100 last:border-0 hover:bg-slate-50">
                    <td className="px-5 py-3 font-mono font-semibold">{q.number}</td>
                    <td className="px-3 text-zinc-600">{q.title || "—"}</td>
                    <td className="px-3"><StatusBadge status={q.status} /></td>
                    <td className="px-3 text-zinc-500">{fmtDate(q.created_at)}</td>
                    <td className="px-3 text-zinc-500">{["approved", "declined"].includes(q.status) ? fmtDate(q.status_changed_at) || "—" : "—"}</td>
                    <td className="px-5 text-right font-mono font-semibold">{money(q.total)}</td>
                  </tr>
                ))}
                {!quotes.length && <tr><td colSpan={6} className="px-5 py-10 text-center text-zinc-400">No quotes for this customer yet.</td></tr>}
              </tbody>
            </table>
          </div>
        </div>
      </div>
      <CustomerDialog open={editOpen} onOpenChange={setEditOpen} customer={c} onSaved={load} />
    </div>
  );
}
